import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalEnum } from '../enums/localstorage';
import { PlannexeAppService } from '@plannexe/shared/services/plannexe-app.service';

/**
 * Root-level safety net for PlanneXe API calls.
 *
 * TokenInterceptor sirf lazy project.module ke HttpClient par chalta ha, is liye
 * root-provided services (detail.service, phase-v2.service, waghera) ki requests
 * baghair Authorization ke jati theen aur backend 401 Unauthorized deta tha.
 *
 * Yeh interceptor sirf plannexe API base URL wali requests par, aur sirf tab jab
 * request par pehle se Authorization na ho, wahi token lagata ha jo TokenInterceptor
 * lagata (e.token, warna tempToken). URL-scoped hone ki wajah se doosre addons ki
 * requests par koi asar nahi parta (2023 wala "addon tabs not working" masla nahi hoga).
 *
 * Base URL match dono par hota ha: (1) jo base app ne runtime par resolve ki (addon se,
 * SaaS/tenant ke hisaab se — e.g. plannexeapi.azurewebsites.net), aur (2) static
 * environment base (dev / login-se-pehle fallback — e.g. engpro2devapi...). Isi liye
 * dev aur SaaS dono par sahi host par token lagta ha aur 401 nahi aata.
 */
@Injectable()
export class PlannexeApiAuthInterceptor implements HttpInterceptor {

  // Injector lazily use karte hain — PlannexeAppService khud HttpClient par depend
  // karti ha, is liye constructor injection circular-DI de sakti ha.
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.headers.has('Authorization') && this.isPlannexeApiUrl(request.url)) {
      const token = localStorage.getItem(LocalEnum.token) || localStorage.getItem(LocalEnum.tempToken);
      if (token) {
        request = request.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
      }
    }
    return next.handle(request);
  }

  private isPlannexeApiUrl(url: string): boolean {
    return this.plannexeApiHosts().some(base => this.hostMatches(url, base));
  }

  /**
   * Dynamic base (addon se resolved — SaaS/tenant-specific) + static environment
   * base (dev / login-se-pehle fallback). Dono cover karte hain.
   */
  private plannexeApiHosts(): string[] {
    let dynamic: string | undefined;
    try {
      dynamic = this.injector.get(PlannexeAppService).plannexeBaseUrl;
    } catch {
      // early boot / DI abhi ready nahi — sirf static base par chalega.
    }
    return [dynamic, environment.plannexe?.baseURL].filter(Boolean) as string[];
  }

  /** Host compare (startsWith ke bajaye) — protocol/slash/path variations se mehfooz. */
  private hostMatches(url: string, base: string): boolean {
    try {
      return new URL(url, window.location.origin).host === new URL(base).host;
    } catch {
      return false;
    }
  }
}
